import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input, FormGroup, Select, Checkbox } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import NumberInput from "components/cck/NumberInput";
import { hasPluginPermission, Language, Plugin, usePermission } from "imports/Utils";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import ContainerTypeSelect from "view/containertypes/components/selects/ContainerTypeSelect";
import { containerBulkEditSchema, ContainerBulkEditSchemaInput, ContainerBulkEditSchemaOutput } from "view/containers/utils/container.schema";
import { FullOrEmpty } from "view/containers/utils/ContainerUtils";

import { BatchUpdateCargosDocument } from "__generated__/view/containers/graphql/mutations.generated";

type ContainerBulkEditDialogProps = {
	open: boolean;
	onClose: () => void;
	containerIds: number[];
	refetch: () => void;
	defaultValues?: {
		containerNumber: string;
		isEmpty: FullOrEmpty;
		temperature: number | null;
		grossWeight: number | null;
		verifiedGrossWeight: number | null;
		sealNumber: string;
		damage: string;
		canSkipperEditLoadedTimestamp: boolean;
		canSkipperEditDischargedTimestamp: boolean;
	};
};

const ContainerBulkEditDialog = ({ open, onClose, containerIds, refetch, defaultValues: values }: ContainerBulkEditDialogProps) => {

	const session = useStoreState(store => store.session);

	const { isSkipperOrShipManager } = usePermission();

	const defaultValues = {
		containerNumber: values?.containerNumber ?? "",
		containerType: null,
		isEmpty: values?.isEmpty ?? null,
		temperature: values?.temperature ?? null,
		weight: null,
		loadedWeight: null,
		grossWeight: values?.grossWeight ?? null,
		verifiedGrossWeight: values?.verifiedGrossWeight ?? null,
		sealNumber: values?.sealNumber ?? "",
		damage: values?.damage ?? "",
		loadingLocation: null,
		loadingReference: "",
		dischargingLocation: null,
		dischargingReference: "",
		canSkipperEditLoadedTimestamp: values?.canSkipperEditLoadedTimestamp ?? true,
		canSkipperEditDischargedTimestamp: values?.canSkipperEditDischargedTimestamp ?? true,
	};

	const { control, handleSubmit, reset } = useForm<ContainerBulkEditSchemaInput, unknown, ContainerBulkEditSchemaOutput>({
		resolver: zodResolver(containerBulkEditSchema),
		defaultValues: defaultValues,
	});

	const [ saveContainers, { loading } ] = useMutation(BatchUpdateCargosDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: ContainerBulkEditSchemaOutput) => {

		const input = {
			ids: containerIds,
			containerNumber: data.containerNumber || null,
			containerType: data.containerType ? data.containerType.isoTypeCode : null,
			empty: data.isEmpty ? data.isEmpty === FullOrEmpty.Empty : null,
			temperature: data.temperature,
			weight: data.weight,
			loadedWeight: data.loadedWeight,
			grossWeight: data.grossWeight,
			verifiedGrossWeight: data.verifiedGrossWeight,
			sealNumber: data.sealNumber || null,
			damage: data.damage || null,
			loadingLocation: data.loadingLocation ? { id: data.loadingLocation.id } : null,
			loadingReference: data.loadingReference || null,
			dischargingLocation: data.dischargingLocation ? { id: data.dischargingLocation.id } : null,
			dischargingReference: data.dischargingReference || null,
			canSkipperEditLoadedTimestamp: data.canSkipperEditLoadedTimestamp,
			canSkipperEditDischargedTimestamp: data.canSkipperEditDischargedTimestamp,
		};

		const cleanInput = Object.fromEntries(
			Object.entries(input).filter(([ _, v ]) => v !== null),
		);

		saveContainers({
			variables: {
				input: cleanInput,
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset(defaultValues);
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: containerIds.length === 1 ? "EDIT_CONTAINER" : "EDIT_CONTAINERS" })}
			form='containerBulkEditForm'
			loading={loading}>
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(save)(event)} id="containerBulkEditForm">
					{!hasPluginPermission(session, "externalCargosManagement") && (
						<React.Fragment>
							{isSkipperOrShipManager && containerIds.length === 1 && (
								<FormGroup label={Language("CONTAINER_NUMBER")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="containerNumber"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{!isSkipperOrShipManager && hasPluginPermission(session, Plugin.USE_CONTAINERS) && (
								<FormGroup label={Language("CARGO_CONTAINER_TYPE")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="containerType"
										control={control}
										render={({ field }) => <ContainerTypeSelect {...field} includeTeu />}
									/>
								</FormGroup>
							)}

							<FormGroup label={Language("CARGO_FULL_OR_EMPTY")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="isEmpty"
									control={control}
									render={({ field }) => (
										<Select
											{ ...field }
											fetchOptions={[ FullOrEmpty.Full, FullOrEmpty.Empty ]}
											getOptionLabel={option => intl.formatMessage({ id: `CARGO_${option}` })}
											disableSearch />
									)} />
							</FormGroup>

							<FormGroup label={Language("CARGO_DESIRED_TEMPERATURE")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="temperature"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput { ...field } addonAfter={"\u00b0C"} decimalScale={2} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							{!isSkipperOrShipManager && (
								<FormGroup label={Language("WEIGHT")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="weight"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput { ...field } decimalScale={3} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{!isSkipperOrShipManager && (
								<FormGroup label={Language("ORDER_LOADED_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="loadedWeight"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput { ...field } decimalScale={3} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{isSkipperOrShipManager && (
								<FormGroup label={Language("CARGO_GROSS_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="grossWeight"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput { ...field } decimalScale={3} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{isSkipperOrShipManager && (
								<FormGroup label={Language("CARGO_VERIFIED_GROSS_WEIGHT")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="verifiedGrossWeight"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput { ...field } decimalScale={3} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{isSkipperOrShipManager && (
								<FormGroup label={Language("CARGO_SEALNUMBER")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="sealNumber"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input { ...field } />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{isSkipperOrShipManager && (
								<FormGroup label={Language("CARGO_DAMAGE")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="damage"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input { ...field } />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{!isSkipperOrShipManager && (
								<FormGroup label={Language("ORDER_LOADING_PLACE")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="loadingLocation"
										control={control}
										render={({ field }) => <LocationSelect { ...field } />} />
								</FormGroup>
							)}

							{!isSkipperOrShipManager && (
								<FormGroup label={Language("LOADING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="loadingReference"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input { ...field } />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{!isSkipperOrShipManager && (
								<FormGroup label={Language("ORDER_DISCHARGING_PLACE")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="dischargingLocation"
										control={control}
										render={({ field }) => <LocationSelect { ...field } />} />
								</FormGroup>
							)}

							{!isSkipperOrShipManager && (
								<FormGroup label={Language("DISCHARGING_REFERENCE")} labelPlacement="left-center" overflow="hidden">
									<Controller
										name="dischargingReference"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Input { ...field } />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}
						</React.Fragment>
					)}

					{!isSkipperOrShipManager && (
						<React.Fragment>
							<FormGroup label={Language("CAN_SKIPPER_EDIT_LOADED_TIMESTAMP")} labelPlacement="left-center" overflow="visible">
								<Controller
									name="canSkipperEditLoadedTimestamp"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>

							<FormGroup label={Language("CAN_SKIPPER_EDIT_DISCHARGED_TIMESTAMP")} labelPlacement="left-center" overflow="visible">
								<Controller
									name="canSkipperEditDischargedTimestamp"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						</React.Fragment>
					)}
				</form>
			</React.Fragment>
		</FormDialog>
	);
};

export default ContainerBulkEditDialog;
